import { FC, useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import mammoth from 'mammoth';
import { Spin, App } from 'antd';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';

// 配置PDF.js worker - 使用 CDN 资源，避免 MIME 类型问题
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DocumentViewerProps {
  documentUrl?: string;
  filename?: string;
  fallbackContent?: string;
}

type FileType = 'txt' | 'pdf' | 'docx' | 'doc' | 'unknown';

const isSafeDocUrl = (url: string): boolean => {
  const normalized = url.trim().replace(/\s+/g, '').toLowerCase();
  if (!normalized) return true;
  if (normalized.startsWith('javascript:') || normalized.startsWith('vbscript:')) return false;
  if (!normalized.startsWith('data:')) return true;
  return /^data:image\/(png|jpe?g|gif|webp);base64,[a-z0-9+/=]+$/i.test(normalized);
};

const sanitizeDocxHtml = (html: string): string => {
  if (typeof window === 'undefined' || !window.DOMParser) return '';

  const parser = new DOMParser();
  const document = parser.parseFromString(html, 'text/html');
  const disallowedTags = ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'textarea', 'select', 'link', 'meta', 'base'];

  disallowedTags.forEach((tag) => {
    document.querySelectorAll(tag).forEach((node) => node.remove());
  });

  document.querySelectorAll('*').forEach((node) => {
    const attributes = Array.from(node.attributes);
    attributes.forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = attr.value;
      if (name.startsWith('on') || name === 'style') {
        node.removeAttribute(attr.name);
        return;
      }
      if ((name === 'href' || name === 'src' || name === 'xlink:href') && !isSafeDocUrl(value)) {
        node.removeAttribute(attr.name);
      }
    });
  });

  return document.body.innerHTML;
};

const resolveFileType = (source?: string): FileType => {
  if (!source) return 'unknown';
  const cleanSource = source.split('?')[0]?.split('#')[0] ?? source;
  const ext = cleanSource.split('.').pop()?.toLowerCase();
  if (ext === 'txt') return 'txt';
  if (ext === 'pdf') return 'pdf';
  if (ext === 'docx') return 'docx';
  if (ext === 'doc') return 'doc';
  return 'unknown';
};

const DocumentViewer: FC<DocumentViewerProps> = ({ documentUrl, filename, fallbackContent = '' }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState<string>('');
  const [error, setError] = useState(false);
  const [fileType, setFileType] = useState<FileType>('unknown');
  const [numPages, setNumPages] = useState<number>(1);

  // 从文件名或URL提取扩展名
  useEffect(() => {
    const typeFromName = resolveFileType(filename);
    const typeFromUrl = resolveFileType(documentUrl);
    setFileType(typeFromName !== 'unknown' ? typeFromName : typeFromUrl);
  }, [filename, documentUrl]);

  // 加载文档内容
  useEffect(() => {
    if (!documentUrl) {
      // 无URL时使用fallback内容
      setContent(fallbackContent);
      return;
    }

    const loadDocument = async () => {
      try {
        setLoading(true);
        setError(false);

        if (fileType === 'txt') {
          // TXT文件：直接fetch
          const response = await fetch(documentUrl);
          const text = await response.text();
          setContent(text);
        } else if (fileType === 'docx') {
          // DOCX文件：使用mammoth转换
          const response = await fetch(documentUrl);
          const arrayBuffer = await response.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          setContent(sanitizeDocxHtml(result.value));
        } else if (fileType === 'doc') {
          // DOC文件：不支持，使用fallback
          setContent(fallbackContent);
          message.warning(t('documentViewer.docNotSupported'));
        }
        // PDF由react-pdf直接处理，不需要预加载
      } catch (err) {
        console.error('Failed to load document:', err);
        setError(true);
        setContent(fallbackContent);
        message.error(t('documentViewer.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    if (fileType !== 'unknown' && fileType !== 'pdf') {
      loadDocument();
    }
  }, [documentUrl, fileType, fallbackContent, t, message]);

  // PDF加载成功回调
  const onDocumentLoadSuccess = ({ numPages: pages }: { numPages: number }) => {
    setNumPages(pages);
  };

  // PDF加载失败回调
  const onDocumentLoadError = (err: Error) => {
    console.error('Failed to load PDF:', err);
    setError(true);
    setContent(fallbackContent);
  };

  // 渲染不同类型的内容
  const renderContent = () => {
    if (!documentUrl || error || fileType === 'unknown') {
      // Fallback：显示纯文本内容
      const textSource = content || fallbackContent;
      const paragraphs = textSource.split('\n\n').filter((p) => p.trim());
      if (!paragraphs.length) {
        return <div className={styles.emptyText}>{t('common.noData', 'No data')}</div>;
      }
      return (
        <div className={styles.textContent}>
          {paragraphs.map((para, idx) => (
            <p key={idx} className={styles.paragraph}>
              {para}
            </p>
          ))}
        </div>
      );
    }

    if (fileType === 'pdf') {
      return (
        <Document
          file={documentUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className={styles.loading}>
              <Spin tip={t('documentViewer.loading')}>
                <div style={{ minHeight: 200 }} />
              </Spin>
            </div>
          }
          error={
            <div className={styles.error}>
              <p>{t('documentViewer.pdfLoadError')}</p>
              {fallbackContent && (
                <div className={styles.textContent}>
                  {fallbackContent.split('\n\n').map((para, idx) => (
                    <p key={idx} className={styles.paragraph}>
                      {para}
                    </p>
                  ))}
                </div>
              )}
              {!fallbackContent && <div className={styles.emptyText}>{t('common.noData', 'No data')}</div>}
            </div>
          }
        >
          {Array.from(new Array(numPages), (_, index) => (
            <Page key={`page_${index + 1}`} pageNumber={index + 1} width={360} className={styles.pdfPage} />
          ))}
        </Document>
      );
    }

    if (fileType === 'docx') {
      return <div className={styles.docxContent} dangerouslySetInnerHTML={{ __html: content }} />;
    }

    // TXT或其他
    const paragraphs = content.split('\n\n').filter((p) => p.trim());
    return (
      <div className={styles.textContent}>
        {paragraphs.map((para, idx) => (
          <p key={idx} className={styles.paragraph}>
            {para}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.viewer}>
      <Spin spinning={loading}>{renderContent()}</Spin>
    </div>
  );
};

export default DocumentViewer;
